import React, { useState } from "react";
import Commonheader from "./components/headers/Common";
import { useAuth } from "./contexts/Authcontext";
import { format } from "date-fns";
import Loading from "./components/common/Loading";
import axios from "axios";
import BlueHeader from "./components/common/BlueHeader";
import { getFirestore, doc, updateDoc } from "firebase/firestore";
import {
  setBetaOptIn,
  clearBetaOptIn,
} from "./components/utils/betaOptIn";
import { runMigrationCleanup } from "./components/utils/migrationCleanup";
import { handOverToNewSite } from "./components/utils/cacheBust";

const Profile = () => {
  const { currentUser, userDocument, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && !currentUser) {
      window.location.href =
        "/login?referURL=" + encodeURIComponent("/profile");
    }
  }, [currentUser, authLoading]);

  const [loading, setLoading] = useState(false);

  const [betaWorking, setBetaWorking] = useState(false);
  const [betaError, setBetaError] = useState(null);

  // The document is the decision, the cookie is a copy of it.
  //
  // The write goes first and everything else follows from it, because the write
  // is the half that reaches their other devices: the field is what this site
  // reads on every sign in to decide whether to set the cookie. Setting the
  // cookie without it would put this browser on the new site and leave their
  // phone behind.
  //
  // Then the cleanup, then the handover — in that order, because the handover
  // navigates away and would cut the cleanup off mid flight.
  const setBetaMembership = async (optIn) => {
    setBetaWorking(true);
    setBetaError(null);

    try {
      await updateDoc(doc(getFirestore(), "Users", currentUser.uid), {
        betaOptIn: optIn,
      });
    } catch (error) {
      console.error("Error saving beta preference:", error);
      setBetaError(
        "Sorry — we couldn't save that. Please try again in a moment."
      );
      setBetaWorking(false);
      return;
    }

    if (!optIn) {
      // They are already on the site clearing the cookie sends them to, so
      // there is nowhere to go. The snapshot listener will re-render this.
      clearBetaOptIn();
      setBetaWorking(false);
      return;
    }

    setBetaOptIn();
    await runMigrationCleanup();
    handOverToNewSite();
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const authToken = await currentUser.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_DBAPI_URL}/api/v1.0/generateStripeCustomerPortalLink`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setLoading(false);
      const { url } = response.data;
      window.location.href = url;
    } catch (error) {
      setLoading(false);
      console.error("Error generating management link:", error);
    }
  };

  return (
    <>
      {" "}
      <BlueHeader title="My Profile" />
      <div className="container-fluid pb-5">
        <Commonheader page="Profile" />

        {currentUser && userDocument ? (
          <>
            <div className="row justify-content-center">
              <div className="col-md-6">
                <h3 className="pt-3 pb-2">User Information</h3>

                <p>
                  You are logged in with{" "}
                  {userDocument.idp.charAt(0).toUpperCase() +
                    userDocument.idp.slice(1)}
                  . If you need to make changes to your email or password, you
                  can do so there.
                </p>
                <div className="form-group mt-4">
                  <label htmlFor="emailAddress" className="font-weight-bold">
                    Your Email:
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="subscriptionStatus"
                    value={userDocument.email}
                    readOnly
                  />
                </div>
                <div className="form-group mt-4">
                  <label htmlFor="userId" className="font-weight-bold">
                    Your User ID:
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="userId"
                    value={currentUser.uid}
                    readOnly
                  />
                  <small className="form-text text-muted">
                    You may be asked for this if you contact support.
                  </small>
                </div>
                {/* Only for an account an operator has let in. `betaEligible`
                    is set from the admin console; everyone else sees nothing
                    here at all, rather than an offer they cannot take. */}
                {userDocument.betaEligible && (
                  <>
                    <div className="d-flex justify-content-between align-items-center pt-3 pb-2 mb-2 mt-4">
                      <h3 className="mb-0">The new departureboard.io</h3>
                    </div>
                    {userDocument.betaOptIn ? (
                      <>
                        <p>
                          You're on the new departureboard.io. If you're reading
                          this, your browser served you this old page from its
                          own cache — reload and you should land on the new site.
                        </p>
                        <p>
                          Had enough? You can come back to this version at any
                          time, on all your devices, and switch again whenever
                          you like.
                        </p>
                        {/* The one thing somebody genuinely loses by switching
                            back, said before they press the button rather than
                            discovered afterwards. */}
                        <p className="font-weight-bold">
                          Anything you changed on the new site — saved journeys,
                          the train you're on — stays there. It won't appear
                          here.
                        </p>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => setBetaMembership(false)}
                          disabled={betaWorking}
                        >
                          {betaWorking ? "Saving..." : "Go back to this version"}
                        </button>
                      </>
                    ) : (
                      <>
                        <p>
                          You've been invited to try the new
                          departureboard.io — rebuilt from scratch, faster, and
                          rather nicer to look at.
                        </p>
                        <p>
                          It's the same account and the same subscription, and
                          your saved journeys come with you. Switching applies
                          everywhere you're signed in, and you can switch back
                          whenever you want from Settings on the new site.
                        </p>
                        {/* Said here too, because this is the decision it is
                            actually about: from this moment anything they do
                            happens over there, and coming back does not bring
                            it with them. */}
                        <p className="font-weight-bold">
                          Anything you change over there stays over there — if
                          you come back to this version later, it won't follow
                          you.
                        </p>
                        <button
                          className="btn btn-primary"
                          onClick={() => setBetaMembership(true)}
                          disabled={betaWorking}
                        >
                          {betaWorking
                            ? "Taking you there..."
                            : "Try the new site"}
                        </button>
                      </>
                    )}
                    {betaError && (
                      <p className="text-danger mt-3">{betaError}</p>
                    )}
                  </>
                )}
                <div className="d-flex justify-content-between align-items-center pt-3 pb-2 mb-2 mt-4">
                  <h3 className="mb-0">Pro</h3>
                  {userDocument.isPro && (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleManageSubscription}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Manage"}
                    </button>
                  )}
                </div>
                {userDocument.isPro ? (
                  <div>
                    <p>
                      Horrah! You're a departureboard.io Pro member, meaning you
                      get a whole host of additional features to help optimise
                      your commute.
                    </p>
                    <p>
                      Need to manage your subscription? Just hit the "Manage"
                      button up above.
                    </p>
                    <div className="form-group mt-4">
                      <label
                        htmlFor="subscriptionStatus"
                        className="font-weight-bold"
                      >
                        {userDocument.cancelAtEndPeriod
                          ? "Subscription Ends"
                          : "Subscription Renews"}
                        :
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="subscriptionStatus"
                        value={format(
                          new Date(userDocument.endPeriod.seconds * 1000),
                          "EEEE do MMMM yyyy"
                        )}
                        readOnly
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <p>
                      Oh no! You're not a departureboard.io Pro member, meaning
                      you're missing out on a whole host of additional features
                      to help optimise your commute.
                    </p>
                    <p>
                      To learn more and subscribe, just{" "}
                      <a href="/pro">click here</a>.
                    </p>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <Loading />
        )}
      </div>
    </>
  );
};

export default Profile;
